import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TOOL_CATEGORIES } from '../../constants/toolCategories';
import useAuthStore from '../../store/authStore';
import {
  HomeIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  CpuChipIcon,
  KeyIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  TrashIcon,
  ChartBarIcon,
  UserPlusIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Upload', href: '/upload', icon: ArrowUpTrayIcon },
  { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
  { name: 'Recycle Bin', href: '/recycle-bin', icon: TrashIcon },
  { name: 'Document Types', href: '/document-types', icon: RectangleStackIcon },
  { name: 'API Keys', href: '/api-keys', icon: KeyIcon },
];

const adminNavigation = [
  { name: 'Statistics', href: '/statistics', icon: ChartBarIcon },
  { name: 'Signup Requests', href: '/signup-requests', icon: UserPlusIcon },
  { name: 'Email Templates', href: '/email-templates', icon: EnvelopeIcon },
  { name: 'Fine-Tuning', href: '/fine-tune', icon: CpuChipIcon },
  { name: 'API Tokens', href: '/api-tokens', icon: KeyIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'API Docs', href: '/admin-api-docs', icon: DocumentTextIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

const helpNavigation = [
  { name: 'Feedback & Support', href: '/feedback-support', icon: QuestionMarkCircleIcon },
];

// TOOL_CATEGORIES imported from ../../constants/toolCategories


export default function MobileDrawer({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuthStore();

  const isOnTools = location.pathname === '/tools' || location.pathname.startsWith('/tools/');
  const [toolsExpanded, setToolsExpanded] = useState(isOnTools);
  const activeCat = searchParams.get('cat') || 'all';

  const isActive = (href) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/');
  };

  const handleNavClick = () => { onClose(); };

  const navLinkClass = (active) =>
    `flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 ${active
      ? 'bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-lg shadow-brand-orange/25'
      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
    }`;

  return (
    <>
      {/* Overlay */}
      <div
        className={`mobile-drawer-overlay ${open ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`mobile-drawer ${open ? 'open' : ''}`}>
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-theme-primary safe-area-top">
          <Link to="/dashboard" className="flex items-center gap-3" onClick={handleNavClick}>
            <img src="/images/lensverse_logo.png" alt="LensVerse" className="h-9 w-9" />
            <span className="text-lg font-bold text-theme-primary">LensVerse</span>
          </Link>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-theme-tertiary text-theme-muted hover:text-theme-primary transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-theme-primary">
          <div className="flex items-center gap-3 p-3 bg-theme-tertiary rounded-xl">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-theme-primary truncate">{user?.username}</p>
              <p className="text-xs text-theme-muted truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          {/* Main Navigation */}
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link key={item.name} to={item.href} onClick={handleNavClick} className={navLinkClass(active)}>
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* ── Tools Section ── */}
          <div className="mt-6 pt-6 border-t border-theme-primary">
            <p className="px-4 mb-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">Tools</p>
            <div className="space-y-1">
              {/* Tool Catalog main link + chevron */}
              <div>
                <div className="flex items-center gap-1">
                  <Link
                    to="/tools"
                    onClick={handleNavClick}
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 flex-1 ${isOnTools
                        ? 'bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-lg shadow-brand-orange/25'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
                      }`}
                  >
                    <WrenchScrewdriverIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium flex-1">Tool Catalog</span>
                  </Link>
                  <button
                    onClick={() => setToolsExpanded(p => !p)}
                    className="p-3 rounded-xl text-theme-muted hover:text-theme-primary hover:bg-theme-tertiary transition-all flex-shrink-0"
                  >
                    <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${toolsExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Category sub-menu */}
                {toolsExpanded && (
                  <div className="mt-2 ml-5 pl-3 border-l-2 border-theme-primary space-y-0.5">
                    {TOOL_CATEGORIES.map(cat => {
                      const isCatActive = isOnTools && activeCat === cat.id;
                      return (
                        <Link
                          key={cat.id}
                          to={cat.id === 'all' ? '/tools' : `/tools?cat=${cat.id}`}
                          onClick={handleNavClick}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${isCatActive
                              ? 'bg-brand-orange/10 text-brand-orange'
                              : 'text-theme-muted hover:text-theme-primary hover:bg-theme-tertiary'
                            }`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isCatActive ? 'bg-brand-orange' : cat.bgColor}`} />
                          {cat.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Admin Section */}
          {user?.is_superuser && (
            <div className="mt-6 pt-6 border-t border-theme-primary">
              <p className="px-4 mb-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                Settings
              </p>
              <div className="space-y-1">
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={handleNavClick}
                      className={`
                        flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200
                        ${active
                          ? 'bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-lg shadow-brand-orange/25'
                          : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-6 pt-6 border-t border-theme-primary">
            <p className="px-4 mb-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Help
            </p>
            <div className="space-y-1">
              {helpNavigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200
                      ${active
                        ? 'bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-lg shadow-brand-orange/25'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-theme-primary safe-area-bottom">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span className="font-medium">Sign out</span>
          </button>
        </div>
      </div>
    </>
  );
}
